import { PENDING_TEAM_INVITE_MODAL_ID } from '@/atoms/pending-team-invite-modal-ids';
import { KINDE_MIGRATION_MODAL_ID } from '@/types/modal-queue.types';
import {
  HEIDI_2_CONFIRM_ACCESS_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
} from '@heidi/agents/contracts';
import { ModalId } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

/**
 * Modal pacing (`release-growth-modal-pacing`, default OFF).
 * Throttles non-essential launch surfaces (intros/spotlights/coachmarks) with a
 * new-user quiet period, active-recording suppress, and per-session cap.
 * Exempt: blocking, trial/billing, first-run onboarding, user-action-armed nudges.
 * User-triggered never paced. Membership is an exempt allowlist (buckets
 * cross priority tiers).
 */

/** Launch surfaces that bypass pacing. Everything else auto-enqueued is paced. */
export const PACING_EXEMPT_MODAL_IDS: ReadonlySet<ModalId> = new Set<ModalId>([
  // T0 completed-copy feedback. Its once-per-clinical-session guard lives in
  // the toolbar writer that creates the request, not in the queue.
  ModalId.ACTION_COPY_FORMATTING_LOSS_POPOVER,
  // Blocking
  ModalId.USER_SELF_ONBOARDING,
  ModalId.T0_TEAM_MEMBER_REQUIRE_SPECIALTY_MODAL,
  ModalId.T1_NETWORK_FIREWALL_ERROR_MODAL,
  KINDE_MIGRATION_MODAL_ID,
  PENDING_TEAM_INVITE_MODAL_ID,
  ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL,
  // Device-connect moment — must not lose the 1/session paced slot to a
  // coincident launch surface (a plug-in is not a browsing moment).
  ModalId.ACTION_DICTAPHONE_CONNECTED_DEVICE_SETUP_POPOVER,
  // Phase-2 setup offer. Arms on a plug-in like the card above, AND at launch
  // for a device already attached (desk-mounted mics have no connect moment).
  // Still exempt: it is setup UI for hardware the user owns (T0
  // ProductFunctionality in the tier register), showOnce-ever with per-device
  // memory, so pacing it would only withhold a mid-session plug-in behind an
  // unrelated nudge. The trade: at launch it ignores the new-user quiet
  // period and the session cap; recording is still held off by the launch
  // row's own `!isClinicalRecordingActive`.
  ModalId.ACTION_DICTAPHONE_CONNECTED_DEVICE_SETUP_V2_POPOVER,
  // Compliance (T2): weekly cadence is its own cooldown, not a pacing slot
  ModalId.CUSTOM_AUDIENCE_HCP_VERIFICATION_REMINDER_MODAL,
  // Trial / billing
  ModalId.CLINICIAN_TRIAL,
  ModalId.PRACTICE_TRIAL,
  ModalId.FREE_TRIAL,
  ModalId.NINETY_DAY_TRIAL_END_MODAL,
  ModalId.NINETY_DAY_TRIAL_END_DISCREET_POPUP,
  ModalId.NINETY_DAY_TRIAL_ENDING_SOON_REMINDER_POPUP,
  ModalId.TRIAL_ENDING_SOON_REMINDER,
  ModalId.TRIAL_END_PLAN_SELECTION,
  ModalId.EVIDENCE_POST_UPGRADE_WELCOME_POPUP,
  ModalId.T3_CUSTOM_AUDIENCE_CLINICIAN_MAX_CREDIT_USAGE_POPOVER,
  // First-run onboarding
  ModalId.CUSTOM_TEMPLATE_ONBOARDING,
  ModalId.SCRIBE_ONBOARDING,
  ModalId.UNIVERSAL_DICTATION_ONBOARDING_MODAL,
  // Work first-visit tour — not a recurring T4 tip (R5 exception)
  ModalId.T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT,
  // Heidi II Evidence tour — the same R5 exception. It is what a clinician who
  // skipped the welcome gets instead of the tour, once-ever and armed by that
  // skip; a slot already spent on an unrelated nudge would drop the hand-off
  // for the session and leave them with no introduction at all.
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  // Heidi II team-access confirmation — the same R5 exception. It is the
  // once-ever prompt a team admin gets to grant Heidi II; a slot already spent
  // on an unrelated nudge would drop it for the session and leave the team
  // without access until the next visit.
  HEIDI_2_CONFIRM_ACCESS_MODAL_ID,
  // Dictate re-engagement card — armed by a completed in-app dictation, not
  // cold-launch eligible; its own trigger gates are stricter than every pacing
  // lever, and showOnce-ever makes the session cap pure loss.
  ModalId.ACTION_DICTATED_GENERAL_REENGAGEMENT_POPOVER,
]);
// Intentionally paced: FIRMWARE_UPDATE, other feature intros, LOW spotlights/coachmarks.

export const isPacedModal = (id: ModalId): boolean => !PACING_EXEMPT_MODAL_IDS.has(id);

export const PACING_MAX_PER_SESSION = 1;

export const PACING_DISMISSED_COUNT_STORAGE_KEY = 'heidi.modalPacing.dismissedCount:v1';

const createSsrSafeSessionStorage = <T>() =>
  createJSONStorage<T>(() => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return window.sessionStorage;
  });

/** PostHog kill switch, fed by ModalPacingSync. Default false until PostHog resolves. */
export const isModalPacingEnabledAtom = atom(false);

/**
 * Dev-tools only: launch surfaces a preview has exempted from pacing, so a
 * nudge can be checked on an account still in its quiet period. Nothing in the
 * product writes it. Survives in-tab reload so the preview does too.
 */
export const devPacingExemptModalIdsAtom = atomWithStorage<ModalId[]>(
  'heidi.modalPacing.devExempt:v1',
  [],
  createSsrSafeSessionStorage<ModalId[]>()
);

/** Per-tab session cap; stamped on DISMISS (not show). Survives in-tab reload via sessionStorage. */
export const pacedModalSeenCountAtom = atomWithStorage<number>(
  PACING_DISMISSED_COUNT_STORAGE_KEY,
  0,
  createSsrSafeSessionStorage<number>()
);
